import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';

// Convex and other providers are initialized inside the root route, under the router context
import { createRouter } from './router';
import './index.css';
// Ensure i18n is initialized as early as possible so translations are available
import './i18n';
import { env } from './env';

// ✅ Initialize Sentry before React renders (production only)
import { initSentry } from './lib/sentry';
if (import.meta.env.PROD) {
  initSentry();
} else {
  console.info('[Sentry] Skipping initialization in development environment');
}

// ✅ Initialize PostHog conditionally (production only + user consent)
// Note: CookieBanner will handle initialization for new users
import { initPostHog } from './lib/posthog';
if (import.meta.env.PROD) {
  // Check if user has already consented (for returning users)
  const cookieConsent = localStorage.getItem('cookie_consent_given');
  const analyticsEnabled = localStorage.getItem('cookie_analytics') !== 'false';
  if (cookieConsent === 'true' && analyticsEnabled) {
    initPostHog();
  }
} else {
  console.info('[PostHog] Skipping initialization in development environment');
}

// Import your Publishable Key
const PUBLISHABLE_KEY = env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Add your Clerk Publishable Key to the .env.local file');
}

// Create the router instance
const router = createRouter();

// Providers are composed in the router's root route component

const RootProviders: React.FC = () => {
  return (
    <React.Suspense fallback={null}>
      <RouterProvider router={router} />
    </React.Suspense>
  );
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootProviders />
  </StrictMode>
);
